'use client';

import { Fragment, useContext, useMemo } from 'react';

import { ComboboxRootContext } from '../context/combobox-root.context';
import type { ComboboxTriggerRenderPropsInterface } from '../types/combobox-trigger.types';

/**
 * Hook for rendering selected values as chips with optional overflow indicator
 *
 * This hook handles:
 * - Filtering visible chips based on limit
 * - Rendering chips for selected values
 * - Showing an overflow indicator for hidden chips
 * - Supporting removal of chips via click
 *
 * @param params - Configuration options for chip rendering
 * @returns JSX element
 */
export const useComboboxChipsAndOverflow = ({
  renderChip,
  renderChipsOverflow,
}: Pick<ComboboxTriggerRenderPropsInterface, 'renderChip' | 'renderChipsOverflow'>) => {
  const { combobox, freeForm, limitChipsShown, readOnly, showLabelForValue } =
    useContext(ComboboxRootContext);

  return useMemo(() => {
    // Early return if not in multi-select mode
    if (!combobox.isMultiSelect || readOnly) {
      return null;
    }

    // Calculate which selected values should be visible based on limits and combobox state
    const visibleValues =
      limitChipsShown && !combobox.open
        ? combobox.selectedValues.slice(0, limitChipsShown)
        : combobox.selectedValues;

    // Calculate number of hidden values for overflow indicator
    const hiddenValuesCount =
      limitChipsShown && !combobox.open
        ? Math.max(0, combobox.selectedValues.length - limitChipsShown)
        : 0;

    // Early return if there's nothing to show
    const shouldRender = visibleValues.length > 0 || hiddenValuesCount > 0;
    if (!shouldRender) {
      return null;
    }

    // Create the chip elements
    const chipElements = visibleValues.map((value, index) => {
      // Get the option object from the optionMap
      let option = combobox.optionsMap.get(value);

      // Handle freeform values and missing options
      if (!option && freeForm) {
        option = value;
      } else if (!option) {
        return undefined;
      }

      // Extract the display text
      const chipText =
        typeof option === 'object' ? (showLabelForValue ? option.label : option.value) : option;

      // Render the chip if a renderer is provided
      return renderChip?.({
        key: value,
        isLast: index === visibleValues.length - 1,
        children: chipText,
        onClick: (event) => {
          event.stopPropagation();
          combobox.onClickInputChipClose(value, event);
        },
      });
    });

    // Create the overflow indicator if needed
    const overflowElement =
      hiddenValuesCount > 0
        ? renderChipsOverflow?.({
            children: `+${hiddenValuesCount}`,
          })
        : null;

    // Combine chips and overflow indicator
    return (
      <Fragment>
        {chipElements}
        {overflowElement}
      </Fragment>
    );
  }, [
    combobox,
    freeForm,
    limitChipsShown,
    readOnly,
    showLabelForValue,
    renderChip,
    renderChipsOverflow,
  ]);
};
